import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Client } from './client';
import type { ModuleUI } from '../module';

// The engine's runtime context: the API client, the registered modules (nav, screens,
// widgets, commands), and the current principal's identity. Screens and hooks read from
// here; tests wrap components in <EngineProvider client={mock}>.

export interface Principal {
  id?: string;
  email?: string | null;
  name?: string | null;
  /** Permission strings ('tickets:assign'); empty = pre-RBAC allow-all. */
  permissions?: string[];
  /** Platform superadmin tier ('platform_owner' | 'platform_admin'), when the token carries it. */
  platformRole?: string;
  /** The caller's built-in tenant membership role ('owner' | 'admin' | 'member' | …), from GET /v1/me. */
  tenantRole?: string | null;
}

export interface EngineCtx {
  client: Client;
  modules: ModuleUI[];
  principal: Principal;
  signOut?: () => void | Promise<void>;
}

const Ctx = createContext<EngineCtx | null>(null);

export function EngineProvider({ client, modules = [], principal = {}, signOut, children }: { client: Client; modules?: ModuleUI[]; principal?: Principal; signOut?: () => void | Promise<void>; children: ReactNode }) {
  const value = useMemo(() => ({ client, modules, principal, signOut }), [client, modules, principal, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEngine(): EngineCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useEngine requires <EngineProvider>');
  return c;
}

export function useApi(): Client {
  return useEngine().client;
}

/** All custom widgets contributed by modules, keyed by widget name. */
export function useWidgets() {
  const { modules } = useEngine();
  return useMemo(() => Object.assign({}, ...modules.map((m) => m.widgets ?? {})) as NonNullable<ModuleUI['widgets']>, [modules]);
}

/** Every module's Home tiles, in module registration order. */
export function useTiles() {
  const { modules } = useEngine();
  return useMemo(() => modules.flatMap((m) => m.tiles ?? []), [modules]);
}

/** Whether the principal holds `permission` (no list = pre-RBAC allow-all; the API still decides). */
export function useCan(permission: string): boolean {
  const perms = useEngine().principal.permissions;
  return !perms || perms.length === 0 || perms.includes(permission) || perms.includes('*');
}
